// src/components/intake/BookPicker.jsx
//
// The author's own manuscripts, one row each. A book with an open submission
// can't be picked: it shows its status and links to the submission instead,
// because a sent-back book is resubmitted from there rather than submitted again.

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import StatusChip from './StatusChip';
import { SP, FONT } from './intakeSx';

export function bookId(book) {
  return book._id || book.id;
}

export function lastEdited(book) {
  const when = book.lastEdited ? new Date(book.lastEdited) : null;
  return when && !Number.isNaN(when.getTime())
    ? `Last edited ${when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : 'Not edited yet';
}

function Thumb({ book }) {
  return (
    <Box
      sx={{
        width: 36,
        height: 50,
        borderRadius: '3px',
        flexShrink: 0,
        overflow: 'hidden',
        background: SP.lavender,
        border: `1px solid ${SP.lineSoft}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: SP.purpleSoft,
      }}
    >
      {book.cover_url ? (
        <Box component="img" src={book.cover_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <MenuBookOutlinedIcon fontSize="small" />
      )}
    </Box>
  );
}

export default function BookPicker({ books, openByDraft, selectedId, onSelect }) {
  return (
    <Box sx={{ display: 'grid', gap: 1.25, mt: 2.5 }} role="radiogroup" aria-label="Your books" data-testid="intake-books">
      {books.map((book) => {
        const id = bookId(book);
        const open = openByDraft[id];
        const selected = selectedId === id;
        const rowSx = {
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          textAlign: 'left',
          p: '10px 14px',
          borderRadius: '7px',
          border: `1.5px solid ${selected ? SP.purple : SP.lineSoft}`,
          background: selected ? SP.lavender : SP.white,
          font: 'inherit',
          color: SP.black,
        };
        const text = (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontFamily: FONT.serif, fontWeight: 600, fontSize: 15, overflowWrap: 'anywhere' }}>
              {book.title || 'Untitled'}
            </Typography>
            <Typography sx={{ fontFamily: FONT.mono, fontSize: 10.5, color: SP.gray, mt: '3px' }}>
              {lastEdited(book)}
            </Typography>
          </Box>
        );

        if (open) {
          return (
            <Box key={id} sx={{ ...rowSx, opacity: 0.92 }} data-testid="intake-book-open">
              <Thumb book={book} />
              {text}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                <StatusChip status={open.status} />
                <Link component={RouterLink} to={`/intake/${open.reservation_number}`} sx={{ fontSize: 12 }}>
                  {open.status === 'changes_requested' ? 'See notes and resubmit' : 'View submission'}
                </Link>
              </Box>
            </Box>
          );
        }

        return (
          <Box
            key={id}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect(book)}
            data-testid="intake-book"
            sx={{
              ...rowSx,
              cursor: 'pointer',
              transition: 'border-color .18s ease, background .18s ease',
              '&:hover': { borderColor: selected ? SP.purple : SP.purpleSoft, background: SP.lavender },
              '&:focus-visible': { outline: `2px solid ${SP.purpleSoft}`, outlineOffset: 2 },
            }}
          >
            <Thumb book={book} />
            {text}
          </Box>
        );
      })}
    </Box>
  );
}
